import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ArrowUpRight,
  Camera, Home, RefreshCw, CheckCircle2, Globe, Star,
  PartyPopper, Compass, Handshake, MessageCircle, Map,
  Check, X, Lightbulb, Brain, Sparkles, Zap, Film, Rocket, Hand,
  Trophy, Search, FlaskConical, Puzzle,
  Palette, Smartphone, Settings, Clapperboard, BarChart3,
  AlertCircle,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useReveal from '../hooks/useReveal';
import { projects } from '../mock';
import homepageImg from '../assets/coco/homepage.png';
import refreshImg from '../assets/coco/refresh.png';
import successImg from '../assets/coco/success.png';
import failureImg from '../assets/coco/failure.png';
import fivestarImg from '../assets/coco/fivestar.png';
import roleCelebrateImg from '../assets/coco/role-celebrate.png';
import roleGuideImg from '../assets/coco/role-guide.png';
import roleEncourageImg from '../assets/coco/role-encourage.png';
import roleSupportImg from '../assets/coco/role-support.jpg';

import slide01 from '../assets/coco/ideations/slide-01.jpg';
import slide02 from '../assets/coco/ideations/slide-02.jpg';
import slide03 from '../assets/coco/ideations/slide-03.jpg';
import slide04 from '../assets/coco/ideations/slide-04.jpg';
import slide05 from '../assets/coco/ideations/slide-05.jpg';
import slide06 from '../assets/coco/ideations/slide-06.jpg';
import slide07 from '../assets/coco/ideations/slide-07.jpg';
import slide08 from '../assets/coco/ideations/slide-08.jpg';
import slide09 from '../assets/coco/ideations/slide-09.jpg';
import slide10 from '../assets/coco/ideations/slide-10.jpg';
import slide11 from '../assets/coco/ideations/slide-11.jpg';
import slide12 from '../assets/coco/ideations/slide-12.jpg';
import aiWallImg from '../assets/coco/ai-explorations/wall.jpg';

const ideationSlides = [slide01, slide02, slide03, slide04, slide05, slide06, slide07, slide08, slide09, slide10, slide11, slide12];

const SECTIONS = ['hero', 'challenge', 'mapping', 'framework', 'personality', 'explorations', 'coordination', 'production', 'impact'];
const SECTION_LABELS = ['Intro', 'Challenge', 'Mapping', 'Framework', 'Personality', 'Explorations', 'Coordination', 'Production', 'Impact'];

export default function CocoCaseStudy() {
  useReveal();
  const project = useMemo(() => projects.find((p) => p.slug === 'coco-integration-tataplay'), []);
  const nextProject = useMemo(() => {
    const idx = projects.findIndex((p) => p.slug === 'coco-integration-tataplay');
    return projects[(idx + 1) % projects.length];
  }, []);

  const [progress, setProgress] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);
  const [activeRole, setActiveRole] = useState('celebrate');

  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  useEffect(() => {
    const onScroll = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(100, Math.round((window.scrollY / total) * 100)) : 0);
      let active = 0;
      SECTIONS.forEach((id, i) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top < window.innerHeight * 0.5) active = i;
      });
      setActiveIdx(active);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (i) => {
    const el = document.getElementById(SECTIONS[i]);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!project) return null;

  const roles = [
    { key: 'celebrate',  Icon: PartyPopper,    title: 'Celebrate',  body: 'Amplify moments that feel like wins — for the user and the product.', where: 'Recharge success · Subscription confirmed · First login', image: roleCelebrateImg, caption: 'Pack changes done · Success state' },
    { key: 'guide',      Icon: Compass,        title: 'Guide',      body: 'Show users where to go next without making them feel lost.',          where: 'First-run tours · Feature discovery · Contextual tips',   image: roleGuideImg,     caption: 'Experience the best of Tata Play · Onboarding' },
    { key: 'encourage',  Icon: Handshake,      title: 'Encourage',  body: 'Warm nudges for mid-journey moments where users might hesitate.',     where: 'NPS prompts · Mid-onboarding · Partial completions',      image: roleEncourageImg, caption: 'Create Profile · Encouraging completion' },
    { key: 'support',    Icon: MessageCircle,  title: 'Support',    body: 'Calm and present when things feel uncertain or empty.',              where: 'Empty states · Error recovery · No-results moments',     image: roleSupportImg,   caption: 'You\u2019re offline · Empty state' },
  ];

  return (
    <div className="App">
      <Navbar />

      {/* Scroll progress */}
      <div className="fixed top-0 left-0 h-[3px] z-[60] transition-[width] duration-100" style={{ width: `${progress}%`, background: 'var(--terracotta)' }} />

      {/* Chapter sidebar dots */}
      <div className="hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 z-40 flex-col gap-3">
        {SECTIONS.map((_, i) => (
          <button key={i} onClick={() => scrollTo(i)} title={SECTION_LABELS[i]} className="group flex items-center gap-3" aria-label={SECTION_LABELS[i]}>
            <span className="text-[10px] tracking-[0.18em] uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-200" style={{ color: 'var(--ink-soft)' }}>
              {SECTION_LABELS[i]}
            </span>
            <span className="rounded-full transition-all duration-300 block" style={{ width: activeIdx === i ? 12 : 8, height: activeIdx === i ? 12 : 8, background: activeIdx === i ? 'var(--terracotta)' : 'var(--border)' }} />
          </button>
        ))}
      </div>

      <article className="pt-28 md:pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/#work" className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase link-underline" style={{ color: 'var(--terracotta-deep)' }}>
            <ArrowLeft size={14} /> Back to Work
          </Link>
        </div>

        {/* HERO */}
        <section id="hero" className="max-w-3xl mx-auto px-6 pt-12 pb-16 mb-12" style={{ borderBottom: '1px solid var(--border)' }}>
          <p className="reveal text-[11px] tracking-[0.22em] uppercase mb-5" style={{ color: 'var(--terracotta)' }}>
            Case Study · Tata Play · Mascot Integration
          </p>
          <h1 className="reveal reveal-delay-1 font-display leading-[1.05] mb-6" style={{ fontSize: 'clamp(40px, 6vw, 64px)', color: 'var(--ink)', fontWeight: 600 }}>
            Coco, <em className="italic" style={{ color: 'var(--terracotta)' }}>with purpose</em>
          </h1>
          <p className="reveal reveal-delay-2 text-[16px] leading-[1.75] mb-8 max-w-[560px]" style={{ color: 'var(--ink-soft)' }}>
            When Tata Play introduced Coco, the goal wasn&apos;t simply to launch a mascot. The goal was much bigger — make Coco a meaningful part
            of the product experience. Not a sticker. Not a campaign. Not a one-time promotion.
          </p>
          <div className="reveal reveal-delay-3 flex flex-wrap gap-2.5 mb-6">
            <MetaPill accent>Assistant Manager - UI/UX</MetaPill>
            <MetaPill>Tata Play · TPMA</MetaPill>
            <MetaPill>Mascot integration system</MetaPill>
            <MetaPill highlight>49L+ impressions</MetaPill>
          </div>
          <p className="reveal reveal-delay-3 text-[12.5px] tracking-[0.08em]" style={{ color: 'var(--ink-soft)' }}>
            <strong className="font-semibold" style={{ color: 'var(--ink)' }}>Surfaces:</strong> Homepage · Pull to refresh · Success states · NPS · Onboarding
          </p>
        </section>

        {/* Cover gradient */}
        <div className="max-w-6xl mx-auto px-6 mb-16">
          <div className="reveal rounded-[22px] overflow-hidden shadow-warm-lg relative" style={{ aspectRatio: '16/7', background: project.gradient }}>
            {project.cover && <img src={project.cover} alt={project.title} className="absolute inset-0 w-full h-full object-cover" />}
          </div>
        </div>

        {/* Hero montage — 5 surface cards */}
        <div className="max-w-5xl mx-auto px-6 mb-16">
          <IllustrationBox icon={Camera} label="Hero montage — Coco across product journeys">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-5">
              <SurfaceTile Icon={Home}          title="Homepage"        sub="Coco as a welcome presence on the main surface" image={homepageImg} phoneAspect />
              <SurfaceTile Icon={RefreshCw}     title="Refresh"         sub="Motion + delight on a habitual gesture"         image={refreshImg}  phoneAspect />
              <SurfaceTile Icon={CheckCircle2}  title="Success"         sub="Coco celebrating with the user"                  image={successImg}  phoneAspect />
              <SurfaceTile Icon={AlertCircle}   title="Failure"         sub="Coco showing up with empathy when things break"  image={failureImg}  phoneAspect />
              <SurfaceTile Icon={Star}          title="NPS"             sub="Coco encouraging feedback"                       image={fivestarImg} phoneAspect />
            </div>
          </IllustrationBox>
        </div>

        {/* CHAPTER 01 — CHALLENGE */}
        <Chapter id="challenge" number="01 — The challenge">
          <H2>
            Not <em className="italic" style={{ color: 'var(--terracotta)' }}>&quot;where can we show Coco?&quot;</em>
          </H2>
          <P>A mascot can easily become visual noise. The question wasn&apos;t where Coco could appear. It was where Coco could <em className="italic">actually help</em>.</P>

          <div className="reveal my-9">
            <QuestionStepper
              items={[
                { n: '01', question: 'Where should Coco appear?',          highlight: false },
                { n: '02', question: 'What should Coco do?',                highlight: false },
                { n: '03', question: 'When should Coco stay invisible?',    highlight: true  },
              ]}
            />
          </div>

          <PullQuote>&ldquo;Visibility alone doesn&apos;t create engagement. Relevance does.&rdquo;</PullQuote>
          <P>Three questions. The third one — when to stay invisible — turned out to be the hardest to answer, and the most important.</P>
        </Chapter>

        {/* CHAPTER 02 — MAPPING */}
        <Chapter id="mapping" number="02 — Mapping the ecosystem">
          <H2>
            Before creating anything, <em className="italic" style={{ color: 'var(--terracotta)' }}>I mapped everything.</em>
          </H2>
          <P>
            Every major user journey across TPMA, the website, and Litmus. The goal wasn&apos;t to find places to put Coco — it was to find moments
            where Coco could genuinely do something: celebrate, guide, encourage, or support.
          </P>

          <IllustrationBox icon={Compass} label="The four roles — tap to switch context">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              {roles.map((r) => {
                const isActive = activeRole === r.key;
                return (
                  <button
                    key={r.key}
                    onClick={() => setActiveRole(r.key)}
                    className="flex flex-col items-start gap-2 p-4 rounded-[12px] text-left transition-all duration-200"
                    style={{
                      background: isActive ? 'var(--terracotta)' : 'var(--bg)',
                      border: `1.5px solid ${isActive ? 'var(--terracotta)' : 'var(--border)'}`,
                      color: isActive ? '#FAF6EE' : 'var(--ink)',
                    }}
                  >
                    <r.Icon size={22} strokeWidth={1.6} />
                    <span className="text-[14px] font-medium">{r.title}</span>
                  </button>
                );
              })}
            </div>
            <div className="rounded-[12px] p-5" style={{ background: '#FAF0E8', border: '1px solid var(--border)' }}>
              {roles.filter((r) => r.key === activeRole).map((r) => (
                <div key={r.key} className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-5 items-center">
                  <div>
                    <p className="text-[14.5px] leading-[1.6] mb-3" style={{ color: 'var(--ink)' }}>{r.body}</p>
                    <p className="text-[12px] tracking-[0.06em]" style={{ color: 'var(--terracotta-deep)' }}>
                      → {r.where}
                    </p>
                  </div>
                  <div className="flex justify-center md:justify-end">
                    <div
                      className="overflow-hidden rounded-[18px] shadow-warm"
                      style={{
                        width: '160px',
                        aspectRatio: '360 / 800',
                        background: '#0F0A14',
                        border: '1.5px solid var(--ink)',
                      }}
                    >
                      <img src={r.image} alt={r.caption} className="block w-full h-full object-cover" loading="lazy" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </IllustrationBox>

          <IllustrationBox icon={Map} label="Coco ideations — touchpoint mapping deck">
            <CocoIdeationsSlideshow />
          </IllustrationBox>

          <div className="grid grid-cols-3 gap-3 my-7">
            <StatCard big="35+" label="opportunities identified across all journeys" />
            <StatCard big="3"   label="platforms: TPMA, Website, Litmus" />
            <StatCard big="4"   label="distinct roles Coco could play" />
          </div>
        </Chapter>

        {/* CHAPTER 03 — THE HARDER DECISION */}
        <Chapter id="framework" number="03 — The harder decision">
          <H2>
            Not every touchpoint <em className="italic" style={{ color: 'var(--terracotta)' }}>deserved Coco.</em>
          </H2>
          <P>
            One of the biggest decisions was deciding where <em className="italic">not</em> to use the mascot. Some journeys benefited from emotion. Others
            benefited from clarity. Mixing them up would weaken both.
          </P>

          <div className="grid grid-cols-1 md:grid-cols-2 rounded-[12px] overflow-hidden my-7" style={{ border: '1px solid var(--border)', gap: 1, background: 'var(--border)' }}>
            <YesNoPanel
              variant="yes"
              title="Coco belongs here"
              items={[
                { tag: 'emotion',  text: 'Success states — celebrate wins' },
                { tag: 'warmth',   text: 'NPS — encourage feedback' },
                { tag: 'presence', text: 'Empty states — need warmth' },
                { tag: 'guide',    text: 'Onboarding — first impressions' },
              ]}
            />
            <YesNoPanel
              variant="no"
              title="Coco stays invisible"
              items={[
                { tag: 'focus',   text: 'High-stakes transactional flows' },
                { tag: 'clarity', text: 'Information-heavy journeys' },
                { tag: 'density', text: 'Cluttered or complex surfaces' },
                { tag: 'trust',   text: 'Error states needing precision' },
              ]}
            />
          </div>

          <Insight icon={Lightbulb}>
            Adding Coco everywhere would weaken its impact. The framework was as much about <strong className="text-white font-medium">restraint</strong> as
            it was about placement.
          </Insight>
        </Chapter>

        <Ornament />

        {/* CHAPTER 04 — PERSONALITY */}
        <Chapter id="personality" number="04 — Defining Coco's personality">
          <H2>
            Before designing screens, <em className="italic" style={{ color: 'var(--terracotta)' }}>I designed behaviours.</em>
          </H2>
          <P>
            Once the touchpoints were identified, a new challenge emerged: a mascot shouldn&apos;t behave the same way everywhere. Coco celebrating
            a recharge success is a different energy to Coco helping someone through an empty state.
          </P>
          <P>
            So instead of jumping to screens, I first mapped how Coco should respond — given the context, and given how the user was likely
            feeling in that moment.
          </P>

          <IllustrationBox icon={Brain} label="The behaviour framework">
            <BehaviourFramework />
            <p className="text-[12px] mt-4 text-center italic" style={{ color: 'var(--ink-soft)' }}>
              Behaviours were defined before a single screen was drawn
            </p>
          </IllustrationBox>
        </Chapter>

        {/* CHAPTER 05 — EXPLORATIONS */}
        <Chapter id="explorations" number="05 — Exploring possibilities fast">
          <H2>
            140+ explorations — <em className="italic" style={{ color: 'var(--terracotta)' }}>before a single production asset existed.</em>
          </H2>
          <P>
            The project moved quickly. Production assets didn&apos;t exist yet, but stakeholders needed something tangible to react to. So instead
            of waiting, I created rapid concept explorations — expressions, poses, scenarios, placements — to make the strategy visible and testable.
          </P>
          <Insight icon={Zap}>
            <strong className="text-white font-medium">140+ explorations</strong> created before production. The goal wasn&apos;t polished output — it was
            giving teams something concrete to point at, argue about, and align around.
          </Insight>
          <IllustrationBox icon={FlaskConical} label="AI exploration wall — 140+ concepts">
            <div
              className="rounded-[12px] overflow-y-auto overflow-x-hidden"
              style={{
                height: 520,
                background: '#1A1014',
                border: '1px solid var(--border)',
                scrollbarWidth: 'thin',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              <img
                src={aiWallImg}
                alt="AI explorations — moodboard wall of 140+ Coco concepts"
                className="block w-full h-auto"
                loading="lazy"
              />
            </div>
            <p className="text-[11.5px] mt-3 text-center italic" style={{ color: 'var(--ink-soft)' }}>
              Scroll inside the wall to explore expressions, poses, scenarios and placements
            </p>
          </IllustrationBox>
        </Chapter>

        {/* CHAPTER 06 — COORDINATION */}
        <Chapter id="coordination" number="06 — Aligning multiple teams">
          <H2>
            The challenge became less about design. <em className="italic" style={{ color: 'var(--terracotta)' }}>More about coordination.</em>
          </H2>
          <P>
            As the project expanded, five different teams got involved — each with their own goals and constraints. Brand cared about consistency.
            Product cared about experience. Tech cared about implementation complexity. Agency needed direction. Litmus needed specs.
          </P>
          <P>The role shifted: creating one integration strategy everyone could align behind, not just handing off a screen.</P>

          <IllustrationBox icon={Handshake} label="Stakeholder ecosystem">
            <StakeholderDiagram />
          </IllustrationBox>

          <Insight icon={Lightbulb}>
            Everyone had different goals. The hardest design decision wasn&apos;t about Coco&apos;s expression — it was{' '}
            <strong className="text-white font-medium">getting five teams to agree</strong> on the same question.
          </Insight>

          {/* 06b — Motion vs Static */}
          <h3 className="reveal font-display text-[26px] md:text-[32px] leading-tight mt-12 mb-3" style={{ color: 'var(--ink)', fontWeight: 600 }}>
            Should Coco <em className="italic" style={{ color: 'var(--terracotta)' }}>move?</em>
          </h3>
          <P>
            Motion increased delight. But it also increased implementation complexity. The answer couldn&apos;t be &quot;always&quot; or &quot;never&quot;
            — it needed to be a framework.
          </P>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <MotionPanel
              variant="motion"
              title="Motion — where it adds value"
              icon={Sparkles}
              items={[
                'Pull to refresh — the animation is the moment',
                'Success states — motion amplifies the emotion',
                'Onboarding — show, don\u2019t tell',
              ]}
            />
            <MotionPanel
              variant="static"
              title="Static — where speed matters"
              icon={Zap}
              items={[
                'NPS prompts — the ask needs focus',
                'Website headers — performance first',
                'Cluttered surfaces — reduce cognitive load',
              ]}
            />
          </div>

          <div
            className="reveal flex items-start gap-3.5 p-5 rounded-[12px] my-9"
            style={{ background: 'var(--surface)', border: '1px dashed var(--border)' }}
          >
            <Film size={18} style={{ color: 'var(--terracotta)', flexShrink: 0, marginTop: 2 }} />
            <p className="text-[13.5px] leading-[1.65]" style={{ color: 'var(--ink-soft)' }}>
              <strong className="font-semibold" style={{ color: 'var(--ink)' }}>Motion showcase — planned for Phase 2 rollout.</strong>{' '}
              GIFs and micro-interactions for pull-to-refresh and success states are part of the next release wave, once the static framework has stabilised across surfaces.
            </p>
          </div>
        </Chapter>

        {/* PRODUCTION */}
        <Chapter id="production">
          <H2>
            From strategy to <em className="italic" style={{ color: 'var(--terracotta)' }}>production.</em>
          </H2>
          <P>Concepts became screens. Screens became builds. The framework held across six surfaces.</P>

          <IllustrationBox icon={Rocket} label="Implementation gallery — Coco across all surfaces">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <SurfaceTile Icon={Home}         title="Homepage"     sub="Banner · celebrate" />
              <SurfaceTile Icon={RefreshCw}    title="Refresh"      sub="Motion · delight" />
              <SurfaceTile Icon={CheckCircle2} title="Success state" sub="Celebrate · emotion" />
              <SurfaceTile Icon={Star}         title="NPS"          sub="Encourage · static" />
              <SurfaceTile Icon={Globe}        title="Website"      sub="Hero · guide" />
              <SurfaceTile Icon={Hand}         title="Onboarding"   sub="Guide · first-run" />
            </div>
          </IllustrationBox>

          <h3 className="reveal font-display text-[26px] md:text-[32px] leading-tight mt-12 mb-3" style={{ color: 'var(--ink)', fontWeight: 600 }}>
            Ensuring consistency <em className="italic" style={{ color: 'var(--terracotta)' }}>at scale.</em>
          </h3>
          <P>With five teams implementing Coco simultaneously across three platforms, quality couldn&apos;t be left to chance.</P>

          <div className="grid grid-cols-2 gap-3 my-6">
            <StatCard big="70+" label="issues identified across platforms before sign-off" />
            <StatCard big="3"   label="full review cycles across TPMA, website, and Litmus" />
          </div>
        </Chapter>

        {/* CHAPTER 07 — IMPACT */}
        <Chapter id="impact" number="07 — Impact">
          <H2>
            What started as a mascot initiative <em className="italic" style={{ color: 'var(--terracotta)' }}>became a product-wide integration.</em>
          </H2>
          <P>
            49 lakh impressions across five surfaces. Three awards. But the number that mattered most was harder to count: five teams, aligned
            behind one question.
          </P>

          <div className="grid grid-cols-3 gap-3 my-7">
            <StatCard big="49L+" label="impressions across all platforms" />
            <StatCard big="70+"  label="QA issues caught before launch" />
            <StatCard big="5"    label="teams under one strategy" />
          </div>

          <div className="reveal rounded-[14px] p-6 my-6" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
            <p className="text-[11px] tracking-[0.22em] uppercase mb-4" style={{ color: 'var(--terracotta)' }}>Recognition</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <AwardCard title="SPOT Award" />
              <AwardCard title="Job Well Done" />
              <AwardCard title="Star Performer" />
            </div>
          </div>

          {/* Reframe */}
          <div className="reveal rounded-[14px] p-6 my-9 text-center" style={{ background: 'var(--ink)', color: '#FAF6EE' }}>
            <p className="text-[11px] tracking-[0.22em] uppercase mb-3" style={{ color: 'var(--ochre)' }}>The reframe</p>
            <p className="text-[15px] mb-2 line-through opacity-70">This was a branding project.</p>
            <p className="text-[14px] opacity-70 mb-2">↓</p>
            <p className="font-display italic text-[20px] md:text-[22px] leading-[1.45]" style={{ color: '#FAF6EE' }}>
              &ldquo;In reality, it became a systems-design challenge.&rdquo;
            </p>
          </div>

          {/* Learnings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 mt-8">
            <LearningCard n="01">
              Mascots work best when they <strong className="font-semibold" style={{ color: 'var(--ink)' }}>serve a purpose</strong> — not just fill a surface.
            </LearningCard>
            <LearningCard n="02">
              <strong className="font-semibold" style={{ color: 'var(--ink)' }}>Consistency requires systems</strong>, not assets. A single illustration without a framework fragments.
            </LearningCard>
            <LearningCard n="03">
              <strong className="font-semibold" style={{ color: 'var(--ink)' }}>Ecosystem thinking</strong> beats screen thinking. Map journeys before designing moments.
            </LearningCard>
            <LearningCard n="04">
              <strong className="font-semibold" style={{ color: 'var(--ink)' }}>Alignment can be harder than design.</strong> Creating the shared framework was the real work.
            </LearningCard>
          </div>
        </Chapter>

        {/* CLOSING */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center" style={{ borderTop: '1px solid var(--border)' }}>
          <p className="font-display italic leading-[1.5] mb-8 mx-auto max-w-[600px]" style={{ fontSize: 'clamp(20px, 2.2vw, 26px)', color: 'var(--ink-soft)' }}>
            &ldquo;The hardest part wasn&apos;t designing the mascot. It was creating a framework that helped multiple teams answer one question:
            where does Coco genuinely improve the experience?&rdquo;
          </p>
          <div className="flex flex-wrap justify-center gap-3.5">
            <Link
              to="/#work"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[13px] transition-colors duration-300"
              style={{ background: 'transparent', color: 'var(--ink)', border: '1.5px solid var(--border)' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--ink-soft)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              All case studies <ArrowRight size={15} />
            </Link>
          </div>
        </section>

        {/* Next / back */}
        <div className="max-w-3xl mx-auto px-6 mt-12 pt-10 flex flex-wrap items-center justify-between gap-6" style={{ borderTop: '1px solid var(--border)' }}>
          <Link to="/#work" className="inline-flex items-center gap-2 text-[13px] tracking-[0.12em] uppercase link-underline" style={{ color: 'var(--ink)' }}>
            <ArrowLeft size={15} /> Back to all work
          </Link>
          {nextProject && (
            <Link to={`/case/${nextProject.slug}`} className="group inline-flex items-center gap-3 rounded-2xl px-5 py-4 transition-all duration-300" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
              <span className="text-right">
                <span className="block text-[11px] tracking-[0.18em] uppercase" style={{ color: 'var(--sage)' }}>Next case study</span>
                <span className="block font-display text-[18px]" style={{ color: 'var(--ink)' }}>{nextProject.title}</span>
              </span>
              <ArrowRight size={18} style={{ color: 'var(--terracotta)' }} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
        </div>
      </article>

      <Footer />
    </div>
  );
}

/* ----- Building blocks ----- */

function MetaPill({ children, accent = false, highlight = false }) {
  let bg = 'var(--surface)', color = 'var(--ink-soft)', border = 'var(--border)';
  if (accent)    { bg = 'var(--terracotta)';   color = '#FAF6EE';            border = 'var(--terracotta)'; }
  if (highlight) { bg = 'var(--ink)';          color = 'var(--ochre)';       border = 'var(--ink)'; }
  return (
    <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-medium" style={{ background: bg, color, border: `1px solid ${border}` }}>
      {children}
    </span>
  );
}

function Chapter({ id, number, children }) {
  return (
    <section id={id} className="max-w-3xl mx-auto px-6 mb-24 scroll-mt-24">
      {number && (
        <p className="reveal text-[11px] tracking-[0.22em] uppercase mb-3" style={{ color: 'var(--ink-soft)' }}>{number}</p>
      )}
      {children}
    </section>
  );
}

function H2({ children }) {
  return (
    <h2 className="reveal font-display leading-[1.2] mb-6" style={{ fontSize: 'clamp(26px, 3vw, 36px)', color: 'var(--ink)', fontWeight: 600 }}>
      {children}
    </h2>
  );
}

function P({ children }) {
  return (<p className="reveal text-[15.5px] leading-[1.8] mb-4" style={{ color: 'var(--ink-soft)' }}>{children}</p>);
}

function PullQuote({ children }) {
  return (
    <blockquote className="reveal my-8 px-6 py-5 rounded-r-[8px]" style={{ borderLeft: '3px solid var(--terracotta)', background: '#FAF0E8' }}>
      <p className="font-display italic text-[19px] leading-[1.55]" style={{ color: 'var(--ink)' }}>{children}</p>
    </blockquote>
  );
}

function IllustrationBox({ icon: Icon, label, children }) {
  return (
    <div className="reveal my-9 rounded-[16px] overflow-hidden" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <div className="flex items-center gap-2 px-5 py-3.5" style={{ borderBottom: '1px solid var(--border)' }}>
        {Icon && <Icon size={14} style={{ color: 'var(--terracotta)' }} />}
        <span className="text-[11px] tracking-[0.14em] uppercase" style={{ color: 'var(--ink-soft)' }}>{label}</span>
      </div>
      <div className="px-6 py-7">{children}</div>
    </div>
  );
}

function Insight({ icon: Icon, children }) {
  return (
    <div className="reveal flex items-start gap-3.5 p-5 rounded-[12px] my-7" style={{ background: 'var(--ink)' }}>
      <Icon size={20} style={{ color: 'var(--ochre)', flexShrink: 0, marginTop: 2 }} />
      <p className="text-[13.5px] leading-[1.65]" style={{ color: '#D4C8B8' }}>{children}</p>
    </div>
  );
}

function Ornament() {
  return (<div className="text-center my-12 tracking-[8px] text-[20px]" style={{ color: 'var(--ochre)' }}>· · ·</div>);
}

function LearningCard({ n, children }) {
  return (
    <div className="reveal rounded-[12px] p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <p className="font-display leading-none mb-2.5" style={{ fontSize: 28, color: 'var(--ochre)', fontWeight: 600 }}>{n}</p>
      <p className="text-[13.5px] leading-[1.6]" style={{ color: 'var(--ink-soft)' }}>{children}</p>
    </div>
  );
}

function Placeholder({ icon: Icon, title, children }) {
  return (
    <div className="w-full text-center flex flex-col items-center gap-3 px-6 py-10 rounded-[12px]" style={{ background: 'linear-gradient(135deg, #F0E8DC 0%, #E8DDD0 100%)', border: '1.5px dashed var(--border)' }}>
      <span className="w-10 h-10 rounded-[10px] flex items-center justify-center" style={{ background: '#F2C4A2' }}>
        <Icon size={18} style={{ color: 'var(--terracotta-deep)' }} />
      </span>
      <span className="font-medium text-[13px]" style={{ color: 'var(--ink)' }}>{title}</span>
      <span className="text-[12px] leading-snug max-w-[420px]" style={{ color: 'var(--ink-soft)' }}>{children}</span>
    </div>
  );
}

function SurfaceTile({ Icon, title, sub, image, phoneAspect = false }) {
  return (
    <div className="rounded-[12px] overflow-hidden flex flex-col" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
      {image && (
        <div className="w-full overflow-hidden flex items-center justify-center" style={{ aspectRatio: phoneAspect ? '9 / 20' : '1 / 1', background: '#0F0A14' }}>
          <img src={image} alt={title} className="block w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="p-3.5 flex-1">
        <div className="flex items-center gap-2 mb-1.5">
          <Icon size={16} style={{ color: 'var(--terracotta)' }} />
          <p className="text-[12.5px] font-medium leading-tight" style={{ color: 'var(--ink)' }}>{title}</p>
        </div>
        <p className="text-[11px] leading-snug" style={{ color: 'var(--ink-soft)' }}>{sub}</p>
      </div>
    </div>
  );
}

function CocoIdeationsSlideshow() {
  const [idx, setIdx] = useState(0);
  const ref = useRef(null);

  const goTo = (i) => {
    const clamped = Math.max(0, Math.min(ideationSlides.length - 1, i));
    setIdx(clamped);
    const el = ref.current;
    if (el) {
      const slide = el.children[clamped];
      if (slide) slide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      const w = el.clientWidth;
      const newIdx = Math.round(el.scrollLeft / w);
      if (newIdx !== idx) setIdx(newIdx);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [idx]);

  return (
    <div>
      <div
        ref={ref}
        className="relative w-full overflow-x-auto rounded-[12px]"
        style={{
          aspectRatio: '16 / 9',
          background: '#1A1014',
          border: '1px solid var(--border)',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          display: 'flex',
        }}
      >
        {ideationSlides.map((src, i) => (
          <div
            key={i}
            className="shrink-0 w-full h-full flex items-center justify-center"
            style={{ scrollSnapAlign: 'start' }}
          >
            <img
              src={src}
              alt={`Coco ideations slide ${i + 1}`}
              className="block w-full h-full object-contain"
              loading={i < 2 ? 'eager' : 'lazy'}
            />
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between mt-4">
        <button
          type="button"
          onClick={() => goTo(idx - 1)}
          disabled={idx === 0}
          className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.06em] rounded-full px-3.5 py-2 transition-colors duration-200"
          style={{
            background: 'var(--bg)',
            border: '1px solid var(--border)',
            color: 'var(--ink)',
            opacity: idx === 0 ? 0.45 : 1,
          }}
        >
          <ArrowLeft size={13} /> Prev
        </button>

        <div className="flex items-center gap-2">
          <span className="text-[11px] tracking-[0.18em] uppercase" style={{ color: 'var(--ink-soft)' }}>
            {String(idx + 1).padStart(2, '0')} / {String(ideationSlides.length).padStart(2, '0')}
          </span>
          <div className="hidden sm:flex items-center gap-1.5 ml-4">
            {ideationSlides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to slide ${i + 1}`}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: idx === i ? 22 : 6,
                  background: idx === i ? 'var(--terracotta)' : 'var(--border)',
                }}
              />
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => goTo(idx + 1)}
          disabled={idx === ideationSlides.length - 1}
          className="inline-flex items-center gap-1.5 text-[12px] tracking-[0.06em] rounded-full px-3.5 py-2 transition-colors duration-200"
          style={{
            background: 'var(--terracotta)',
            border: '1px solid var(--terracotta)',
            color: '#FAF6EE',
            opacity: idx === ideationSlides.length - 1 ? 0.45 : 1,
          }}
        >
          Next <ArrowRight size={13} />
        </button>
      </div>
    </div>
  );
}

function QuestionStepper({ items }) {
  return (
    <div className="relative">
      {/* Horizontal/vertical connector line behind nodes */}
      <div
        className="hidden md:block absolute left-0 right-0"
        style={{ top: 19, height: 2, background: 'linear-gradient(90deg, var(--border) 0%, var(--terracotta) 100%)', zIndex: 0 }}
      />
      <div
        className="md:hidden absolute top-0 bottom-0"
        style={{ left: 19, width: 2, background: 'linear-gradient(180deg, var(--border) 0%, var(--terracotta) 100%)', zIndex: 0 }}
      />
      <ol className="relative grid grid-cols-1 md:grid-cols-3 gap-y-5 md:gap-x-6 z-[1]">
        {items.map((it) => (
          <li key={it.n} className="flex md:flex-col items-start md:items-start gap-3.5">
            {/* Numbered node */}
            <span
              className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-display font-semibold text-[14px]"
              style={{
                background: it.highlight ? 'var(--terracotta)' : 'var(--surface)',
                color: it.highlight ? '#FAF6EE' : 'var(--ink)',
                border: `1.5px solid ${it.highlight ? 'var(--terracotta)' : 'var(--border)'}`,
                boxShadow: it.highlight ? '0 8px 18px -10px rgba(196,98,45,0.45)' : 'none',
              }}
            >
              {it.n}
            </span>
            <div className="md:mt-2">
              <p
                className="text-[10px] tracking-[0.22em] uppercase mb-1.5"
                style={{ color: it.highlight ? 'var(--terracotta-deep)' : 'var(--sage)' }}
              >
                Question {it.n}
              </p>
              <p
                className="text-[14px] font-medium leading-snug"
                style={{ color: 'var(--ink)' }}
              >
                {it.question}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function QuestionChip({ n, question, highlight = false }) {
  return (
    <div className="rounded-[12px] p-4" style={{ background: highlight ? '#FAF0E8' : 'var(--surface)', border: `1px solid ${highlight ? 'var(--terracotta)' : 'var(--border)'}` }}>
      <p className="text-[10px] tracking-[0.22em] uppercase mb-2" style={{ color: highlight ? 'var(--terracotta-deep)' : 'var(--sage)' }}>{n}</p>
      <p className="text-[13.5px] font-medium leading-snug" style={{ color: 'var(--ink)' }}>{question}</p>
    </div>
  );
}

function StatCard({ big, label }) {
  return (
    <div className="rounded-[12px] p-4 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
      <p className="font-display leading-none mb-2" style={{ fontSize: 'clamp(26px, 3vw, 34px)', color: 'var(--terracotta)', fontWeight: 600 }}>{big}</p>
      <p className="text-[11.5px] leading-snug" style={{ color: 'var(--ink-soft)' }}>{label}</p>
    </div>
  );
}

function AwardCard({ title }) {
  return (
    <div className="flex items-center gap-2.5 rounded-[10px] p-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
      <Trophy size={18} style={{ color: 'var(--ochre)' }} />
      <p className="text-[13px] font-medium" style={{ color: 'var(--ink)' }}>{title}</p>
    </div>
  );
}

function YesNoPanel({ variant, title, items }) {
  const isYes = variant === 'yes';
  const accent = isYes ? 'var(--sage)' : '#8A7A60';
  return (
    <div className="p-5" style={{ background: isYes ? '#FAF0E8' : '#EDE8E0' }}>
      <div className="flex items-center gap-1.5 mb-4 text-[10px] tracking-[0.14em] uppercase font-medium" style={{ color: isYes ? 'var(--terracotta)' : accent }}>
        {isYes ? <Check size={12} /> : <X size={12} />} {title}
      </div>
      <ul className="space-y-2.5">
        {items.map((it) => (
          <li key={it.text} className="flex items-start gap-2 text-[13px] leading-[1.5]" style={{ color: 'var(--ink-soft)' }}>
            <span className="shrink-0 text-[10px] tracking-[0.06em] uppercase rounded-md px-1.5 py-0.5 mt-0.5" style={{ background: 'rgba(196,98,45,0.12)', color: 'var(--terracotta-deep)' }}>
              {it.tag}
            </span>
            {it.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

function MotionPanel({ variant, title, icon: Icon, items }) {
  const isMotion = variant === 'motion';
  return (
    <div className="rounded-[12px] p-5" style={{ background: isMotion ? '#FAF0E8' : 'var(--surface)', border: `1px solid ${isMotion ? '#E8C9A8' : 'var(--border)'}` }}>
      <div className="flex items-center gap-2 mb-3 text-[12px] font-semibold" style={{ color: isMotion ? 'var(--terracotta-deep)' : 'var(--ink)' }}>
        <Icon size={15} /> {title}
      </div>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it} className="text-[13px] leading-[1.55]" style={{ color: 'var(--ink-soft)' }}>→ {it}</li>
        ))}
      </ul>
    </div>
  );
}

function BehaviourFramework() {
  return (
    <svg width="100%" viewBox="0 0 620 180" role="img" aria-label="Behaviour framework">
      <defs>
        <marker id="bf-ar" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M2 1L8 5L2 9" fill="none" stroke="context-stroke" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </marker>
      </defs>
      {/* Step 1: Context */}
      <rect x="14"  y="50" width="160" height="80" rx="12" fill="#FAF0E8" stroke="#E8C9A8" />
      <text x="94"  y="76"  textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="12" fontWeight="600" fill="#A4501F">CONTEXT</text>
      <text x="94"  y="98"  textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="12" fill="#5C4A38">What just</text>
      <text x="94"  y="114" textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="12" fill="#5C4A38">happened?</text>
      {/* Arrow */}
      <line x1="178" y1="90" x2="218" y2="90" stroke="#C4622D" strokeWidth="1.6" markerEnd="url(#bf-ar)" />
      {/* Step 2: User emotion */}
      <rect x="222" y="50" width="160" height="80" rx="12" fill="#E8F0EA" stroke="#C5D8C8" />
      <text x="302" y="76"  textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="12" fontWeight="600" fill="#3A5A42">USER EMOTION</text>
      <text x="302" y="98"  textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="12" fill="#5C4A38">How is the user</text>
      <text x="302" y="114" textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="12" fill="#5C4A38">likely feeling?</text>
      {/* Arrow */}
      <line x1="386" y1="90" x2="426" y2="90" stroke="#C4622D" strokeWidth="1.6" markerEnd="url(#bf-ar)" />
      {/* Step 3: Coco response */}
      <rect x="430" y="50" width="176" height="80" rx="12" fill="#C4622D" />
      <text x="518" y="76"  textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="12" fontWeight="600" fill="#FAF6EE">COCO RESPONSE</text>
      <text x="518" y="98"  textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="12" fill="#FADDCC">Celebrate?</text>
      <text x="518" y="114" textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="12" fill="#FADDCC">Guide? Support?</text>
    </svg>
  );
}

function StakeholderDiagram() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-center">
      <div className="grid grid-cols-1 gap-2">
        <TeamPill Icon={Palette}      title="Brand consistency" />
        <TeamPill Icon={Smartphone}   title="Product experience" />
        <TeamPill Icon={Settings}     title="Tech complexity" />
      </div>
      <div className="text-center px-4 py-6 rounded-[14px]" style={{ background: 'var(--terracotta)', color: '#FAF6EE' }}>
        <p className="text-[11px] tracking-[0.18em] uppercase mb-1.5" style={{ color: '#FADDCC' }}>Integration strategy</p>
        <p className="font-display text-[20px] leading-tight" style={{ color: '#FAF6EE', fontWeight: 600 }}>Design Lead</p>
      </div>
      <div className="grid grid-cols-1 gap-2">
        <TeamPill Icon={Clapperboard} title="Agency direction" />
        <TeamPill Icon={BarChart3}    title="Litmus specs" />
        <TeamPill Icon={Search}       title="QA sign-off" />
      </div>
    </div>
  );
}

function TeamPill({ Icon, title }) {
  return (
    <div className="flex items-center gap-2.5 rounded-[10px] px-3.5 py-2.5" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
      <Icon size={16} style={{ color: 'var(--terracotta)' }} />
      <p className="text-[12.5px] font-medium" style={{ color: 'var(--ink)' }}>{title}</p>
    </div>
  );
}
